import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { format, subDays, parseISO } from 'date-fns';

export interface StepEntry {
  id: string;
  user_id: string;
  steps: number;
  logged_date: string;
  source: string;
  created_at: string;
}

export function useStepHistory(userId?: string) {
  const [loading, setLoading] = useState(false);
  const [stepHistory, setStepHistory] = useState<StepEntry[]>([]);

  /**
   * Fetch step history for the user
   */
  const fetchStepHistory = useCallback(async (days: number = 30) => {
    if (!userId) return [];

    setLoading(true);
    try {
      const startDate = format(subDays(new Date(), days), 'yyyy-MM-dd');
      
      const { data, error } = await supabase
        .from('step_history')
        .select('*')
        .eq('user_id', userId)
        .gte('logged_date', startDate)
        .order('logged_date', { ascending: false });

      if (error) throw error;
      
      // Cast to our type since types.ts hasn't regenerated yet
      const entries = (data || []) as unknown as StepEntry[];
      setStepHistory(entries);
      return entries;
    } catch (error) {
      console.error('[StepHistory] Failed to fetch:', error);
      return [];
    } finally {
      setLoading(false);
    }
  }, [userId]);

  /**
   * Save or update step count for a specific date
   * Uses upsert to handle re-syncing the same day
   */
  const saveSteps = useCallback(async (
    steps: number,
    date: Date = new Date(),
    source: string = 'health_sync'
  ): Promise<boolean> => {
    if (!userId) return false;

    try {
      const loggedDate = format(date, 'yyyy-MM-dd');
      
      const { error } = await supabase
        .from('step_history')
        .upsert({
          user_id: userId,
          steps,
          logged_date: loggedDate,
          source,
        }, {
          onConflict: 'user_id,logged_date',
        });

      if (error) throw error;
      
      console.log('[StepHistory] Saved steps:', steps, 'for', loggedDate);
      return true;
    } catch (error) {
      console.error('[StepHistory] Failed to save:', error);
      return false;
    }
  }, [userId]);

  /**
   * Bulk save steps from health sync (multiple days at once)
   */
  const bulkSaveSteps = useCallback(async (
    entries: { steps: number; date: string }[],
    source: string = 'health_sync'
  ): Promise<boolean> => {
    if (!userId || entries.length === 0) return false;

    try {
      const records = entries.map(entry => ({
        user_id: userId,
        steps: entry.steps,
        logged_date: entry.date,
        source,
      }));

      const { error } = await supabase
        .from('step_history')
        .upsert(records, {
          onConflict: 'user_id,logged_date',
        });

      if (error) throw error;
      
      console.log('[StepHistory] Bulk saved', entries.length, 'entries');
      return true;
    } catch (error) {
      console.error('[StepHistory] Bulk save failed:', error);
      return false;
    }
  }, [userId]);

  /**
   * Get today's step count
   */
  const getTodaySteps = useCallback(async (): Promise<number> => {
    if (!userId) return 0;

    try {
      const today = format(new Date(), 'yyyy-MM-dd');
      
      const { data, error } = await supabase
        .from('step_history')
        .select('steps')
        .eq('user_id', userId)
        .eq('logged_date', today)
        .single();

      if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows
      
      return (data as any)?.steps || 0;
    } catch (error) {
      console.error('[StepHistory] Failed to get today:', error);
      return 0;
    }
  }, [userId]);

  /**
   * Delete a step entry
   */
  const deleteSteps = useCallback(async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('step_history')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setStepHistory(prev => prev.filter(e => e.id !== id));
      return true;
    } catch (error) {
      console.error('[StepHistory] Failed to delete:', error);
      return false;
    }
  }, []);

  return {
    loading,
    stepHistory,
    fetchStepHistory,
    saveSteps,
    bulkSaveSteps,
    getTodaySteps,
    deleteSteps,
  };
}
